import Swiper from 'swiper';
import { Navigation, Thumbs } from 'swiper/modules';
import { swiperNavigation } from '../modules/swiperModules';

const SLIDE_CLASSES = {
    product:
        'swiper-slide bg-background-secondary aspect-square desktop:h-full desktop:aspect-auto group [&:not(.swiper-slide-active)>img]:ml-1',
    default:
        'swiper-slide bg-background-secondary aspect-[4/3] group [&:not(.swiper-slide-active)>img]:ml-1',
};

const MAIN_IMAGE_CLASSES = {
    product: 'rounded-medium w-full h-full mix-blend-multiply object-contain',
    default: 'rounded-medium w-full h-full mix-blend-multiply object-contain',
};

const LIGHTBOX_IMAGE_CLASSES =
    'h-full w-full object-cover origin-[0_0] cursor-zoom-in [&.-zoommed]:cursor-zoom-out select-none transition-all';

const THUMB_IMAGE_CLASSES = 'h-full w-full object-cover';

const createImageElement = (image, className) => {
    const img = document.createElement('img');
    img.src = image.src;
    img.alt = image.alt || '';
    img.className = className;

    if (image.srcset) {
        img.srcset = image.srcset;
    }

    return img;
};

const createSlide = (image, slideClass, imgClass) => {
    const slide = document.createElement('div');
    slide.className = slideClass;
    slide.appendChild(createImageElement(image, imgClass));

    return slide;
};

class Gallery {
    constructor(el) {
        if (!el) {
            return;
        }

        this.root = el;
        this.init();
    }

    init() {
        this.rootParent = this.root.parentElement;
        this.context =
            this.rootParent?.dataset.context === 'product' ? 'product' : 'default';
        this.lightboxButton = this.root.querySelector('[js-lightbox-button]');
        this.lighboxEl = this.rootParent?.querySelector('[js-lightbox]');
        this.closeLightboxButton =
            this.lighboxEl?.querySelector('[js-close-lighbox]');
        this.lighboxThumbnails = this.lighboxEl?.querySelector(
            '.lightbox-thumbnails'
        );
        this.lightboxGallery =
            this.lighboxEl?.querySelector('.lightbox-gallery');
        this.lightboxGalleryImgs =
            this.lightboxGallery?.querySelectorAll('img') || [];
        this.lightboxGalleryArrows =
            this.lighboxEl?.querySelector('.slider-arrows');
        this.arrowsEl = this.root.querySelector('.slider-arrows');
        this.mainWrapper = this.root.querySelector('.swiper-wrapper');
        this.lightboxWrapper =
            this.lightboxGallery?.querySelector('.swiper-wrapper');
        this.thumbnailsWrapper =
            this.lighboxThumbnails?.querySelector('.swiper-wrapper');

        this.swiperInstance = null;
        this.lightboxThumbnailsSwiperInstance = null;
        this.lightboxSwiperInstance = null;
        this.lightboxActiveClass = '-active';
        this.onLightboxImgClickBound = this.onLightboxImgClick.bind(this);
        this.onLightboxKeydownsBound = this.onLightboxKeydowns.bind(this);

        this.initLightbox();
        this.initSwiper();
        this.events();

        if (this.rootParent) {
            this.rootParent.style.opacity = '1';
        }
    }

    destroy() {
        this.unbindLightboxImageEvents();
        this.swiperInstance?.destroy(true, true);
        this.lightboxThumbnailsSwiperInstance?.destroy(true, true);
        this.lightboxSwiperInstance?.destroy(true, true);

        this.swiperInstance = null;
        this.lightboxThumbnailsSwiperInstance = null;
        this.lightboxSwiperInstance = null;
    }

    setImages(images) {
        if (!Array.isArray(images) || !images.length) {
            return;
        }

        this.destroy();
        this.renderImages(images);
        this.initLightbox();
        this.initSwiper();
        this.events();

        if (this.rootParent) {
            this.rootParent.style.opacity = '1';
        }
    }

    renderImages(images) {
        const slideClass = SLIDE_CLASSES[this.context] || SLIDE_CLASSES.default;
        const mainImageClass =
            MAIN_IMAGE_CLASSES[this.context] || MAIN_IMAGE_CLASSES.default;

        if (this.mainWrapper) {
            this.mainWrapper.replaceChildren(
                ...images.map(image =>
                    createSlide(image, slideClass, mainImageClass)
                )
            );
        }

        if (this.lightboxWrapper) {
            this.lightboxWrapper.replaceChildren(
                ...images.map(image =>
                    createSlide(
                        image,
                        'swiper-slide bg-background-secondary aspect-square group overflow-hidden',
                        LIGHTBOX_IMAGE_CLASSES
                    )
                )
            );
        }

        if (this.thumbnailsWrapper) {
            this.thumbnailsWrapper.className = `swiper-wrapper ${
                images.length < 11 ? 'desktop:justify-center!' : ''
            }`;
            this.thumbnailsWrapper.replaceChildren(
                ...images.map(image => {
                    const slide = document.createElement('div');
                    slide.className =
                        'swiper-slide desktop:w-[120px]! bg-background-secondary aspect-square group rounded-[1.8px] border-t border-t-transparent [&.swiper-slide-thumb-active]:border-t-border-accent transition-all cursor-pointer';
                    slide.appendChild(
                        createImageElement(
                            {
                                ...image,
                                src: image.thumb_src || image.src,
                                srcset: '',
                            },
                            THUMB_IMAGE_CLASSES
                        )
                    );

                    return slide;
                })
            );
        }

        this.lightboxGalleryImgs =
            this.lightboxGallery?.querySelectorAll('img') || [];
    }

    initLightbox() {
        if (this.lighboxThumbnails) {
            this.lightboxThumbnailsSwiperInstance = new Swiper(
                this.lighboxThumbnails,
                {
                    breakpoints: {
                        0: {
                            slidesPerView: 2.5,
                        },
                        1024: {
                            slidesPerView: 'auto',
                        },
                    },
                }
            );
        }

        if (this.lightboxGallery) {
            this.lightboxSwiperInstance = new Swiper(this.lightboxGallery, {
                slidesPerView: 1,
                speed: 1000,
                modules: [Thumbs, Navigation],
                thumbs: {
                    swiper: this.lightboxThumbnailsSwiperInstance,
                },
                ...swiperNavigation(this.lightboxGalleryArrows),
            });

            this.lightboxSwiperInstance.on('slideChange', e => {
                this.swiperInstance?.slideTo(e.activeIndex);

                if (this.lightboxGalleryImgs) {
                    this.lightboxGalleryImgs.forEach(img => {
                        if (!img._zoomState) {
                            img._zoomState = {
                                scale: 1,
                                x: 0,
                                y: 0,
                                zoomed: false,
                            };
                        }
                        const state = img._zoomState;

                        if (state.zoomed) {
                            state.scale = 1;
                            state.x = 0;
                            state.y = 0;
                            state.zoomed = false;

                            this.applyTransform(img, state);
                            img.classList.remove('-zoommed');
                        }
                    });
                }
            });
        }
    }

    initSwiper() {
        let args = {
            slidesPerView: 1,
            modules: [Navigation],
            speed: 700,
        };

        if (this.arrowsEl) {
            args = { ...args, ...swiperNavigation(this.arrowsEl) };
        }

        this.swiperInstance = new Swiper(this.root, args);

        this.swiperInstance.on('slideChange', e => {
            this.lightboxSwiperInstance?.slideTo(e.activeIndex);
        });
    }

    events() {
        if (this.closeLightboxButton && !this.closeLightboxButton.dataset.bound) {
            this.closeLightboxButton.dataset.bound = '1';
            this.closeLightboxButton.addEventListener(
                'click',
                this.handleCloseLightbox.bind(this)
            );
        }

        if (this.lightboxButton && !this.lightboxButton.dataset.bound) {
            this.lightboxButton.dataset.bound = '1';
            this.lightboxButton.addEventListener(
                'click',
                this.handleOpenLightbox.bind(this)
            );
        }

        if (!this.keydownBound) {
            document.addEventListener(
                'keydown',
                this.onLightboxKeydownsBound
            );
            this.keydownBound = true;
        }

        this.bindLightboxImageEvents();
    }

    bindLightboxImageEvents() {
        this.unbindLightboxImageEvents();

        this.lightboxGalleryImgs.forEach(img => {
            img.addEventListener('click', this.onLightboxImgClickBound);
        });
    }

    unbindLightboxImageEvents() {
        this.lightboxGalleryImgs.forEach(img => {
            img.removeEventListener('click', this.onLightboxImgClickBound);
        });
    }

    handleOpenLightbox() {
        this.lighboxEl?.classList.add(this.lightboxActiveClass);
    }

    handleCloseLightbox() {
        this.lighboxEl?.classList.remove(this.lightboxActiveClass);
    }

    onLightboxKeydowns(e) {
        if (!this.lighboxEl?.classList.contains(this.lightboxActiveClass)) {
            return;
        }

        const { key } = e;

        if (key === 'Escape') {
            this.handleCloseLightbox();
        } else if (key === 'ArrowLeft') {
            this.lightboxSwiperInstance?.slidePrev();
        } else if (key === 'ArrowRight') {
            this.lightboxSwiperInstance?.slideNext();
        }
    }

    onLightboxImgClick(e) {
        const img = e.currentTarget;

        if (!img._zoomState) {
            img._zoomState = {
                scale: 1,
                x: 0,
                y: 0,
                zoomed: false,
            };
        }

        const state = img._zoomState;

        if (state.zoomed) {
            state.scale = 1;
            state.x = 0;
            state.y = 0;
            state.zoomed = false;

            this.applyTransform(img, state);
            img.classList.remove('-zoommed');
            return;
        }

        const rect = img.getBoundingClientRect();

        const mx = e.clientX - rect.left;
        const my = e.clientY - rect.top;

        const newScale = 2.5;

        state.x = mx - (mx - state.x) * (newScale / state.scale);
        state.y = my - (my - state.y) * (newScale / state.scale);

        state.scale = newScale;
        state.zoomed = true;

        this.applyTransform(img, state);
        img.classList.add('-zoommed');
    }

    applyTransform(img, state) {
        img.style.transform = `translate(${state.x}px, ${state.y}px) scale(${state.scale})`;
    }
}

export const initProductGallery = el => {
    if (!el) {
        return null;
    }

    if (el._productGalleryInstance) {
        el._productGalleryInstance.destroy();
        el._productGalleryInstance = null;
    }

    const instance = new Gallery(el);
    el._productGalleryInstance = instance;

    return instance;
};

export const updateProductGallery = (root, images) => {
    if (!root || !Array.isArray(images) || !images.length) {
        return null;
    }

    const instance = root._productGalleryInstance || initProductGallery(root);

    instance?.setImages(images);

    return instance;
};

window.mdaInitProductGallery = initProductGallery;
window.mdaUpdateProductGallery = updateProductGallery;

document.querySelectorAll('.js-gallery').forEach(el => {
    initProductGallery(el);
});
